<template>
  <base-label :class="labelClass" :for="name">
    {{ label }}
    <div class="select-div">
      <select
        :name="name"
        :value="modelValue"
        @input="$emit('update:modelValue', ($event.target as HTMLSelectElement).value)"
        v-bind="$attrs"
        class="block p-3 w-full h-12 bg-white border rounded disabled:text-gray-600 disabled:bg-gray-100"
        :class="{ 'mt-2': label }"
      >
        <option v-if="placeholder" :disabled="!allowEmpty" value>
          {{ placeholder }}
        </option>
        <option
          v-for="option in countryCodeOptions"
          :value="option.code"
          :key="option.code"
          :selected="modelValue == option.code"
        >
          {{ option.label }}
        </option>
      </select>
    </div>
  </base-label>
</template>

<script setup lang="ts">
import { customList } from 'country-codes-list';
import { computed } from 'vue';

defineOptions({
  inheritAttrs: false,
})

const props = withDefaults(
  defineProps<{
    modelValue?: string | null
    allowEmpty?: boolean
    placeholder?: string | boolean
    label?: string
    labelClass?: string | string[]
  }>(),
  {
    allowEmpty: true,
    placeholder: 'Select',
    label: 'Country Code',
  }
)

defineEmits<{
  (e: 'update:modelValue', value: string): void
}>()

// Generate country code options from the country-codes-list package
const countryCodesData = customList(
  'countryCallingCode',
  '{countryCallingCode}|{countryNameEn}'
)

// Convert to array of unique dial codes
const countryCodeOptions = computed(() => {
  const dialCodes = new Set<string>()

  for (const callingCode of Object.keys(countryCodesData)) {
    dialCodes.add(`+${callingCode}`)
  }

  // Sort numerically
  return Array.from(dialCodes)
    .map((code) => ({ code, label: code }))
    .sort((a, b) => {
      const numA = parseInt(a.code.replace('+', ''))
      const numB = parseInt(b.code.replace('+', ''))
      return numA - numB
    })
})

const name = computed(() => `mobile-${props.label?.toLowerCase().replace(/\s+/g, '-')}`)
</script>

<style lang="postcss">
.select-div {
  @apply relative;
}
.select-div:after {
  @apply content-['∨'] absolute right-3 top-1/2 pb-1 text-gray-400 font-bold pointer-events-none -translate-x-1/2 -translate-y-1/2 scale-x-150 scale-y-90;
}
select {
  @apply appearance-none;
}
</style>
